import { useState, useRef } from 'react';

export default function EditPanel({ pages = [], uploadedFiles = [], isProcessing, baseUrl, onEditSuccess }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [rect, setRect] = useState(null); // {x,y,w,h} in displayed image pixels
  const [drawing, setDrawing] = useState(false);
  const [moving, setMoving] = useState(false);
  const startRef = useRef(null);
  const imgRef = useRef(null);
  const overlayRef = useRef(null);
  const [text, setText] = useState('');
  const [fontSize, setFontSize] = useState(12);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(100);

  const selected = pages[selectedIndex];

  const clientToImageCoords = (clientX, clientY) => {
    const img = imgRef.current;
    const rectEl = img.getBoundingClientRect();
    const x = clientX - rectEl.left;
    const y = clientY - rectEl.top;
    return { x, y, displayWidth: rectEl.width, displayHeight: rectEl.height };
  };

  const handleMouseDown = (e) => {
    if (!selected) return;
    const { x, y } = clientToImageCoords(e.clientX, e.clientY);
    // If click inside existing rect, start move
    if (rect && x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h) {
      setMoving(true);
      startRef.current = { x, y, origRect: { ...rect } };
    } else {
      setDrawing(true);
      startRef.current = { x, y };
      setRect({ x, y, w: 0, h: 0 });
    }
  };

  const handleMouseMove = (e) => {
    if (!selected) return;
    const { x, y } = clientToImageCoords(e.clientX, e.clientY);
    if (drawing && startRef.current) {
      const sx = startRef.current.x;
      const sy = startRef.current.y;
      const newRect = { x: Math.min(sx, x), y: Math.min(sy, y), w: Math.abs(x - sx), h: Math.abs(y - sy) };
      setRect(newRect);
    } else if (moving && startRef.current) {
      const dx = x - startRef.current.x;
      const dy = y - startRef.current.y;
      const orig = startRef.current.origRect;
      setRect({ ...orig, x: orig.x + dx, y: orig.y + dy });
    }
  };

  const handleMouseUp = () => {
    setDrawing(false);
    setMoving(false);
    startRef.current = null;
  };

  const applyEdit = async () => {
    if (!rect || !selected) return alert('Draw an area to edit first');
    // find file corresponding to selected.originFile
    const file = uploadedFiles.find(f => f.name === selected.originFile);
    if (!file) return alert('Original file not found in uploaded files. Upload the file first using Add PDF Files.');

    setBusy(true);
    try {
      // map displayed pixels to PDF points
      const img = imgRef.current;
      const displayWidth = img.naturalWidth ? img.clientWidth : img.width; // clientWidth gives displayed size
      const displayHeight = img.clientHeight;
      const scaleX = selected.origWidth / displayWidth;
      const scaleY = selected.origHeight / displayHeight;

      const pdfX = rect.x * scaleX;
      const pdfWidth = rect.w * scaleX;
      // PDF origin is bottom-left, image origin is top-left
      const pdfY = selected.origHeight - (rect.y + rect.h) * scaleY;
      const pdfHeight = rect.h * scaleY;

      const edits = [{ page: selected.pageNumber, x: Math.round(pdfX), y: Math.round(pdfY), width: Math.round(pdfWidth), height: Math.round(pdfHeight), text, fontSize: Number(fontSize) }];

      const form = new FormData();
      form.append('file', file);
      form.append('edits', JSON.stringify(edits));

      const res = await fetch(`${baseUrl}/api/edit-pdf`, { method: 'POST', body: form });
      if (!res.ok) {
        const err = await res.json().catch(()=>null);
        throw new Error(err && err.error ? err.error : 'Edit request failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `edited-${file.name}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      
      if (onEditSuccess) {
        onEditSuccess(file.name);
      }
    } catch (e) {
      alert('Edit failed: ' + e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-light p-3 rounded border">
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-3">
        <h5 className="mb-0 fw-bold text-secondary">
          <i className="bi bi-pencil-square me-2"></i>PDF Editor
        </h5>
        <span className="badge text-bg-light border align-self-start align-self-md-center">
          {pages.length} {pages.length === 1 ? 'page' : 'pages'} loaded
        </span>
      </div>

      {pages.length === 0 ? (
        <div className="d-flex flex-column align-items-center justify-content-center border border-2 border-dashed rounded p-5 bg-white text-muted" style={{ minHeight: '420px' }}>
          <i className="bi bi-file-earmark-plus display-5 mb-3"></i>
          <p className="m-0">Upload PDF files first, then return here to edit a page.</p>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-12">
            <div className="d-flex gap-2 pb-2" style={{ overflowX: 'auto' }}>
              {pages.map((p, idx) => (
                <button
                  key={p.id}
                  type="button"
                  className={`bg-white border p-1 rounded text-start ${idx === selectedIndex ? 'border-primary border-2' : ''}`}
                  style={{ cursor: 'pointer', minWidth: 112 }}
                  onClick={() => { setSelectedIndex(idx); setRect(null); }}
                >
                  <img src={p.src} alt={`thumb-${idx}`} style={{ height: 86, display: 'block', margin: '0 auto' }} />
                  <div className="small text-truncate mt-1" style={{ maxWidth: 100 }}>{p.originFile}</div>
                  <span className="badge bg-primary-subtle text-primary-emphasis border">Page {p.pageNumber}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="col-12 col-xl-9">
            <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2 mb-2">
              <div className="btn-group btn-group-sm" role="group" aria-label="PDF zoom controls">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setZoom((current) => Math.max(75, current - 25))}
                >
                  <i className="bi bi-zoom-out"></i>
                </button>
                <button type="button" className="btn btn-outline-secondary disabled">
                  {zoom}%
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setZoom((current) => Math.min(250, current + 25))}
                >
                  <i className="bi bi-zoom-in"></i>
                </button>
              </div>

              <input
                type="range"
                className="form-range"
                min="75"
                max="250"
                step="25"
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                style={{ maxWidth: 260 }}
                aria-label="PDF zoom level"
              />
            </div>

            <div className="bg-white border rounded p-2 text-center overflow-auto" style={{ maxHeight: '82vh' }}>
              {selected && (
                <div
                  ref={overlayRef}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  style={{ position: 'relative', display: 'inline-block' }}
                >
                  <img
                    ref={imgRef}
                    src={selected.fullResSrc}
                    alt="page-full"
                    style={{ display: 'block', width: `${zoom}%`, maxWidth: 'none', height: 'auto' }}
                  />
                  {rect && (
                    <div style={{ position: 'absolute', left: rect.x, top: rect.y, width: rect.w, height: rect.h, border: '2px dashed #0d6efd', backgroundColor: 'rgba(13,110,253,0.08)', pointerEvents: 'none' }}>
                      <div style={{ position: 'absolute', left: 4, top: 4, color: '#000', fontSize: 12 }}>{text}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="col-12 col-xl-3">
            <div className="card bg-white border h-100">
              <div className="card-body">
                <h6 className="fw-semibold mb-3">Edit Options</h6>

                <div className="mb-3">
                  <label className="form-label fw-semibold small">Text to add</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows="4"
                    placeholder="Type replacement text"
                    value={text}
                    onChange={e => setText(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold small">Font size</label>
                  <input
                    className="form-control form-control-sm"
                    type="number"
                    min="6"
                    max="72"
                    value={fontSize}
                    onChange={e => setFontSize(e.target.value)}
                  />
                </div>

                <div className="small text-muted border rounded p-2 mb-3 bg-light">
                  Click and drag on the page to mark the area where the text should be placed.
                </div>

                <div className="d-grid gap-2">
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => setRect(null)} disabled={!rect || busy || isProcessing}>
                    Clear Area
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={applyEdit} disabled={busy || isProcessing}>
                    {busy ? 'Editing...' : 'Apply Edit & Download'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
